import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  Client,
  Conversation,
  ConversationUpdateReason,
  Message,
  Paginator,
  Participant,
  ParticipantUpdateReason,
  User,
} from '@twilio/conversations';
import { Observable, Subject, Subscription } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { subjectsComplete, unsubscribeAll } from 'src/app/common/app.helpers';
import { MessengerMessage } from 'src/app/components/event-messenger/interfaces/messenger.interface';
import { MessengerService } from 'src/app/components/event-messenger/services/messenger.service';
import { UserInfo } from 'src/app/interfaces';
import {
  isNotificationChannel,
  messageBelongsToNotificationChannel,
  sortMessagesByCreationTime,
  sortMessengerMessagesByCreationTime,
} from '../../helpers/chat.helpers';
import { ConversationsAdapter } from '../../helpers/conversations.adapter';
import { computeConversationEffectiveTitle, getTwilioMessageAuthorUser } from '../../helpers/twilio-message.helpers';
import {
  isGroupConversationForMessenger,
  twilioToMessengerMessageAdapter,
} from '../../helpers/twilio-messenger.helpers';
import { ChatEngineLifeСycleService } from '../../interfaces/chat-engine/chat-engine-lifecycle-service';
import {
  ChatChannelType,
  ChatConversation,
  ChatEngineHandle,
  ChatTraceData,
} from '../../interfaces/chat-model.interface';
import { TwilioChannelsService } from './twilio-channels.service';
import { TwilioChatEngineHandle } from './twilio-chat-engine.handle';
import { TwilioClientAccessService } from './twilio-client-access-service';
import { TwilioErrorService, TwilioEventHandlers, TwilioInternalOperations } from './twilio-error.service';
import { TwilioUsersService } from './twilio-users.service';
import {
  TwilioConversationUpdated,
  TwilioMessageUpdated,
  TwilioParticipantUpdated,
  TwilioUserAttributes,
} from './twilio.interfaces';

@Injectable()
export class TwilioMessengerService extends MessengerService implements OnDestroy {
  public get messengerChanges(): Observable<MessengerMessage[]> {
    return this.messengerChanges$.asObservable();
  }

  public get messengerChannelConnectionStateChanged(): Observable<boolean> {
    return this.chatEngineLifeCycleService.connectionStatusChanged;
  }

  private messengerRawMessages: Message[] = [];
  private messengerMessages: MessengerMessage[] = [];

  private messengerChanges$: Subject<MessengerMessage[]> = new Subject();

  private engineReadinessSubscription$: Subscription | null = null;
  private otherSubscriptions$: Subscription[] = [];

  private chatEngineLifeCycleService = inject(ChatEngineLifeСycleService);
  private twilioClientAccessService = inject(TwilioClientAccessService);
  private twilioErrorService = inject(TwilioErrorService);
  private twilioUsersService = inject(TwilioUsersService);
  private twilioChannelsService = inject(TwilioChannelsService);
  private sessionService = inject(SessionService);

  public constructor() {
    super();

    this.subscribeToEngineNotReady();
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.otherSubscriptions$);
    if (this.engineReadinessSubscription$) {
      this.engineReadinessSubscription$.unsubscribe();
    }
    subjectsComplete([this.messengerChanges$]);
  }

  private subscribeToEngineNotReady(): void {
    // Note: do not register this subscription in the main container
    this.engineReadinessSubscription$ = this.chatEngineLifeCycleService.engineReadinessChanged.subscribe(
      (engineHandle: ChatEngineHandle) => {
        if (engineHandle.isValid()) {
          this.subscribeToInitialChannelListLoaded(engineHandle as TwilioChatEngineHandle);
          this.subscribeToUserEvents();
          this.subscribeToConversationRenamed();
        } else {
          unsubscribeAll(this.otherSubscriptions$);
          this.messengerRawMessages = [];
          this.messengerMessages = [];
          this.messengerChanges$.next(this.messengerMessages);
        }
      },
    );
  }

  private subscribeToInitialChannelListLoaded(engineHandle: TwilioChatEngineHandle): void {
    this.otherSubscriptions$.push(
      this.twilioChannelsService.initialConversationsListLoaded.subscribe(
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        async (initialConversations: ChatConversation[]) => {
          if (engineHandle.isValid()) {
            try {
              await this.buildInitialMessengerContent(engineHandle, initialConversations);

              this.subscribeToConversationEvents();
              this.subscribeToMessageEvents();
              this.subscribeToParticipantEvents();
            } catch (error) {
              this.twilioErrorService.onTwilioInternalOperationFailed(
                TwilioInternalOperations.MESSENGER_INITIALIZATION,
                error,
                {} as ChatTraceData,
              );
            }
          }
        },
      ),
    );
  }

  private async buildInitialMessengerContent(
    engineHandle: TwilioChatEngineHandle,
    initialConversations: ChatConversation[],
  ): Promise<void> {
    const messages: (Message | null)[] = await Promise.all(
      initialConversations
        .filter((c: ChatConversation) => c.channelType !== ChatChannelType.NOTIFICATION)
        .map(async (c: ChatConversation) => {
          if (engineHandle.isValid() && c.channel.channel_external_id) {
            const twilioConversation: Conversation = await engineHandle.client.getConversationByUniqueName(
              c.channel.channel_external_id,
            );
            return await this.getRawLastMessageFromConversation(twilioConversation);
          }
          return null;
        }),
    );

    if (engineHandle.isValid()) {
      this.messengerRawMessages = messages.filter((m: Message | null) => m !== null);
      await this.emitMessengerChanges();
    }
  }

  private subscribeToConversationEvents(): void {
    const twilioClient: Client = this.twilioClientAccessService.getTwilioClient();

    twilioClient.on('conversationJoined', (conversation: Conversation) => {
      if (isNotificationChannel(conversation)) {
        return;
      }
      if (conversation.state.current === 'active') {
        this.handleConversationJoined(conversation).catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(TwilioEventHandlers.MESSENGER_CONVERSATION_JOINED, error, {
            channelUniqueName: conversation.uniqueName,
          } as ChatTraceData);
        });
      }
    });

    twilioClient.on('conversationUpdated', (event: TwilioConversationUpdated) => {
      if (isNotificationChannel(event.conversation)) {
        return;
      }
      if (event.updateReasons.some((reason: ConversationUpdateReason) => reason === 'state')) {
        if (event.conversation.state.current === 'active') {
          this.handleConversationJoined(event.conversation).catch((error: unknown) => {
            this.twilioErrorService.onTwilioEventHandlerFailed(
              TwilioEventHandlers.MESSENGER_CONVERSATION_UPDATED,
              error,
              {
                channelUniqueName: event.conversation.uniqueName,
              } as ChatTraceData,
            );
          });
        }
      }
    });

    twilioClient.on('participantJoined', (newParticipant: Participant) => {
      if (isNotificationChannel(newParticipant.conversation)) {
        return;
      }
      if (newParticipant.conversation.state.current === 'active') {
        this.handleParticipantJoined(newParticipant).catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(TwilioEventHandlers.MESSENGER_PARTICIPANT_JOINED, error, {
            channelUniqueName: newParticipant.conversation.uniqueName,
            participantId: newParticipant.identity,
          } as ChatTraceData);
        });
      }
    });

    twilioClient.on('participantLeft', (leavingParticipant: Participant) => {
      if (isNotificationChannel(leavingParticipant.conversation)) {
        return;
      }
      if (leavingParticipant.conversation.state.current === 'active') {
        this.handleParticipantLeft(leavingParticipant).catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(TwilioEventHandlers.MESSENGER_PARTICIPANT_LEFT, error, {
            channelUniqueName: leavingParticipant.conversation.uniqueName,
            participantId: leavingParticipant.identity,
          } as ChatTraceData);
        });
      }
    });

    twilioClient.on('conversationRemoved', (conversation: Conversation) => {
      if (!isNotificationChannel(conversation)) {
        this.handleConversationRemoved(conversation).catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(
            TwilioEventHandlers.MESSENGER_CONVERSATION_REMOVED,
            error,
            {
              channelUniqueName: conversation.uniqueName,
            } as ChatTraceData,
          );
        });
      }
    });
  }

  private async handleConversationJoined(conversation: Conversation): Promise<void> {
    // Deduplicate network noise - don't join the same conversation multiple times
    const existingChannelIndex: number = this.messengerRawMessages.findIndex(
      (m: Message) => m.conversation.uniqueName === conversation.uniqueName,
    );
    if (existingChannelIndex === -1) {
      // Now we are sure it's a unique conversation
      const lastMessage: Message | null = await this.getRawLastMessageFromConversation(conversation);
      if (lastMessage) {
        this.messengerRawMessages.push(lastMessage);
        await this.emitMessengerChanges();
      }
    }
  }

  private async handleParticipantJoined(newParticipant: Participant): Promise<void> {
    const existingMessage: MessengerMessage = this.messengerMessages.find(
      (message: MessengerMessage) => message.channelUniqueName === newParticipant.conversation.uniqueName,
    );
    if (existingMessage) {
      const newUser: User = await newParticipant.getUser();
      const userAttributes: TwilioUserAttributes = this.twilioUsersService.currentUserAttributes;
      const blockedUserIds: string[] = userAttributes.blocked || [];

      const newUserInfo: UserInfo = ConversationsAdapter.adaptTwilioUserToUserInfo(newUser, blockedUserIds);
      existingMessage.conversationParticipants.push(newUserInfo);

      await this.updateMessengerMessageOnExternalEvents(existingMessage);
    }
  }

  private async handleParticipantLeft(leavingParticipant: Participant): Promise<void> {
    const existingMessage: MessengerMessage = this.messengerMessages.find(
      (message: MessengerMessage) => message.channelUniqueName === leavingParticipant.conversation.uniqueName,
    );
    if (existingMessage) {
      const participantIndex: number = existingMessage.conversationParticipants.findIndex(
        (u: UserInfo) => u.userId === leavingParticipant.identity,
      );
      if (participantIndex !== -1) {
        existingMessage.conversationParticipants.splice(participantIndex, 1);
        await this.updateMessengerMessageOnExternalEvents(existingMessage);
      }
    }
  }

  private async handleConversationRemoved(conversation: Conversation): Promise<void> {
    const existingChannelIndex: number = this.messengerRawMessages.findIndex(
      (m: Message) => m.conversation.uniqueName === conversation.uniqueName,
    );
    if (existingChannelIndex !== -1) {
      this.messengerRawMessages.splice(existingChannelIndex, 1);
      await this.emitMessengerChanges();
    }
  }

  private async updateMessengerMessageOnExternalEvents(message: MessengerMessage): Promise<void> {
    message.channelFriendlyName = computeConversationEffectiveTitle(
      message.channelFriendlyName,
      message.conversationParticipants,
      this.sessionService.userId,
    );
    message.isGroupMessage = isGroupConversationForMessenger(message.conversationParticipants);
    await this.emitMessengerChanges();
  }

  private async getRawLastMessageFromConversation(twilioConversation: Conversation): Promise<Message | null> {
    if (twilioConversation.lastMessage && twilioConversation.lastMessage.index !== undefined) {
      const messagePaginator: Paginator<Message> = await twilioConversation.getMessages(1);
      return messagePaginator.items.length > 0 ? messagePaginator.items[0] : null;
    } else {
      return null;
    }
  }

  private subscribeToMessageEvents(): void {
    const twilioClient: Client = this.twilioClientAccessService.getTwilioClient();

    twilioClient.on('messageAdded', (message: Message) => {
      if (!messageBelongsToNotificationChannel(message)) {
        this.handleMessengerAddedChanges(message).catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(TwilioEventHandlers.MESSENGER_MESSAGE_ADDED, error, {
            channelUniqueName: message.conversation.uniqueName,
            messageId: message.sid,
          } as ChatTraceData);
        });
      }
    });

    twilioClient.on('messageRemoved', (message: Message) => {
      if (!messageBelongsToNotificationChannel(message)) {
        this.handleMessengerRemovedChanges(message).catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(TwilioEventHandlers.MESSENGER_MESSAGE_REMOVED, error, {
            channelUniqueName: message.conversation.uniqueName,
            messageId: message.sid,
          } as ChatTraceData);
        });
      }
    });

    twilioClient.on('messageUpdated', (event: TwilioMessageUpdated) => {
      if (!messageBelongsToNotificationChannel(event.message)) {
        this.handleMessengerUpdatedChanges(event.message).catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(TwilioEventHandlers.MESSENGER_MESSAGE_UPDATED, error, {
            channelUniqueName: event.message.conversation.uniqueName,
            messageId: event.message.sid,
          } as ChatTraceData);
        });
      }
    });
  }

  private async handleMessengerAddedChanges(message: Message): Promise<void> {
    const messageChannelUniqueName: string = message.conversation.uniqueName;
    const existingChannelIndex: number = this.messengerRawMessages.findIndex(
      (messengerMessage: Message) => messengerMessage.conversation.uniqueName === messageChannelUniqueName,
    );
    if (existingChannelIndex !== -1) {
      this.messengerRawMessages[existingChannelIndex] = message;
    } else {
      this.messengerRawMessages.push(message);
    }

    await this.emitMessengerChanges();
  }

  private async handleMessengerRemovedChanges(messageToRemove: Message): Promise<void> {
    const indexOfMessage: number = this.messengerRawMessages.findIndex(
      (message: Message) => messageToRemove.sid === message.sid,
    );
    if (indexOfMessage === -1) {
      return;
    }

    const twilioConversation: Conversation = await this.twilioChannelsService.resolveTwilioConversation(
      messageToRemove.conversation.uniqueName,
    );

    const messages: Paginator<Message> = await twilioConversation.getMessages(1);
    if (messages.items.length > 0) {
      this.messengerRawMessages.splice(indexOfMessage, 1, messages.items[0]);
    } else {
      this.messengerRawMessages.splice(indexOfMessage, 1);
    }
    this.messengerRawMessages = sortMessagesByCreationTime(this.messengerRawMessages);

    await this.emitMessengerChanges();
  }

  private async handleMessengerUpdatedChanges(updatedMessage: Message): Promise<void> {
    const indexOfMessage: number = this.messengerRawMessages.findIndex(
      (message: Message) => updatedMessage.sid === message.sid,
    );
    if (indexOfMessage === -1) {
      return;
    }

    this.messengerRawMessages[indexOfMessage] = updatedMessage;

    await this.emitMessengerChanges();
  }

  private subscribeToParticipantEvents(): void {
    const twilioClient: Client = this.twilioClientAccessService.getTwilioClient();

    twilioClient.on('participantUpdated', (event: TwilioParticipantUpdated) => {
      if (
        event.participant.identity === this.sessionService.userId &&
        !isNotificationChannel(event.participant.conversation)
      ) {
        if (event.updateReasons.some((r: ParticipantUpdateReason) => r === 'lastReadMessageIndex')) {
          this.participantReadMessageIndexActualized(event.participant);
        }
      }
    });
  }

  private participantReadMessageIndexActualized(participant: Participant): void {
    const messageChannelUniqueName: string = participant.conversation.uniqueName;

    const existingMessengerMessageIndex: number = this.messengerMessages.findIndex(
      (messengerMessage: MessengerMessage) => messengerMessage.channelUniqueName === messageChannelUniqueName,
    );
    if (existingMessengerMessageIndex !== -1) {
      const channelLastMessage: MessengerMessage = this.messengerMessages[existingMessengerMessageIndex];
      if (channelLastMessage.isChannelUnread) {
        const existingRawMessageIndex: number = this.messengerRawMessages.findIndex(
          (messengerRawMessage: Message) => messengerRawMessage.conversation.uniqueName === messageChannelUniqueName,
        );
        if (existingRawMessageIndex !== -1) {
          const rawChannelLastMessage: Message = this.messengerRawMessages[existingRawMessageIndex];
          if (rawChannelLastMessage.index <= participant.lastReadMessageIndex) {
            channelLastMessage.isChannelUnread = false;
            this.messengerMessages = [...this.messengerMessages];
            this.messengerChanges$.next(this.messengerMessages);
          }
        }
      }
    }
  }

  private subscribeToUserEvents(): void {
    this.otherSubscriptions$.push(
      this.twilioUsersService.currentUserInfoUpdated.subscribe((updatedUserInfo: UserInfo) => {
        this.updateMessengerAfterUserUpdate(updatedUserInfo);
      }),
    );

    this.otherSubscriptions$.push(
      this.twilioUsersService.otherUserInfoUpdated.subscribe((updatedUserInfo: UserInfo) => {
        this.updateMessengerAfterUserUpdate(updatedUserInfo);
      }),
    );

    this.otherSubscriptions$.push(
      this.twilioUsersService.blockedUsersListChanged.subscribe(() => {
        this.updateMessengerAfterBlockedUsersListChanged().catch((error: unknown) => {
          this.twilioErrorService.onTwilioEventHandlerFailed(
            TwilioEventHandlers.MESSENGER_BLOCKED_LIST_UPDATED,
            error,
            {} as ChatTraceData,
          );
        });
      }),
    );
  }

  private updateMessengerAfterUserUpdate(updatedUserInfo: UserInfo): void {
    let messengerUpdated = false;

    const newMessengerMessages: MessengerMessage[] = this.messengerMessages.map(
      (messengerMessage: MessengerMessage) => {
        const affected: boolean = messengerMessage.conversationParticipants.some(
          (u: UserInfo) => u.userId === updatedUserInfo.userId,
        );
        if (affected) {
          messengerUpdated = true;
          const newParticipants: UserInfo[] = messengerMessage.conversationParticipants.map((u: UserInfo) => {
            return u.userId === updatedUserInfo.userId ? updatedUserInfo : u;
          });

          return {
            ...messengerMessage,
            messageAuthor:
              messengerMessage.messageAuthor.userId === updatedUserInfo.userId
                ? updatedUserInfo
                : messengerMessage.messageAuthor,
            conversationParticipants: newParticipants,
            channelFriendlyName: computeConversationEffectiveTitle(null, newParticipants, this.sessionService.userId),
          };
        } else {
          return messengerMessage;
        }
      },
    );

    if (messengerUpdated) {
      this.messengerMessages = newMessengerMessages;
      this.messengerChanges$.next(this.messengerMessages);
    }
  }

  private async updateMessengerAfterBlockedUsersListChanged(): Promise<void> {
    // Nothing fancy to be done, simply trigger update to active filtering rules
    await this.emitMessengerChanges();
  }

  private subscribeToConversationRenamed(): void {
    this.twilioChannelsService.conversationRenamed.subscribe((conversation: ChatConversation) => {
      const existingMessage: MessengerMessage = this.messengerMessages.find(
        (message: MessengerMessage) => message.channelUniqueName === conversation.channel.channel_external_id,
      );
      if (existingMessage) {
        void this.updateMessengerMessageOnExternalEvents(existingMessage);
      }
    });
  }

  private async emitMessengerChanges(): Promise<void> {
    // TODO: think maybe this re-conversion can be avoided on each update
    const messengerMessages: MessengerMessage[] = await this.adaptTwilioMessages(this.messengerRawMessages);

    this.messengerMessages = this.filterMessages(messengerMessages);
    this.messengerChanges$.next(this.messengerMessages);
  }

  private filterMessages(messages: MessengerMessage[]): MessengerMessage[] {
    const userAttributes: TwilioUserAttributes | null = this.twilioUsersService.currentUserAttributes;
    if (userAttributes && (userAttributes.blocked || userAttributes.muted)) {
      const blockedUserIds: Set<string> = userAttributes.blocked ? new Set(userAttributes.blocked) : new Set();
      return messages.filter((message: MessengerMessage) => {
        if (message.channelType === ChatChannelType.CONVERSATION) {
          return (
            message.isGroupMessage ||
            !message.conversationParticipants.some((participant: UserInfo) => blockedUserIds.has(participant.userId))
          );
        } else {
          return true;
        }
      });
    } else {
      // Clone container to properly handle OnPush updates
      return [...messages];
    }
  }

  private async adaptTwilioMessages(messages: Message[]): Promise<MessengerMessage[]> {
    const messengerMessages: MessengerMessage[] = await Promise.all(
      messages.map(async (m: Message) => await this.adaptTwilioMessage(m)),
    );
    return sortMessengerMessagesByCreationTime(messengerMessages);
  }

  private async adaptTwilioMessage(message: Message): Promise<MessengerMessage> {
    const currentChannelUsers: UserInfo[] = await this.getTwilioChannelUsers(message.conversation);
    const userAttributes: TwilioUserAttributes = this.twilioUsersService.currentUserAttributes;
    const blockedUserIds: string[] = userAttributes.blocked || [];

    const messageAuthorInfo: UserInfo = await getTwilioMessageAuthorUser(
      this.twilioClientAccessService.getTwilioClient(),
      message,
      currentChannelUsers,
      blockedUserIds,
    );

    return twilioToMessengerMessageAdapter(message, messageAuthorInfo, currentChannelUsers, this.sessionService.userId);
  }

  private async getTwilioChannelUsers(channel: Conversation): Promise<UserInfo[]> {
    const participants: Participant[] = await channel.getParticipants();
    const userAttributes: TwilioUserAttributes = this.twilioUsersService.currentUserAttributes;
    const blockedUserIds: string[] = userAttributes.blocked || [];

    return await ConversationsAdapter.adaptTwilioParticipantsToUsersInfo(participants, blockedUserIds);
  }
}
